// بطاقة الحساب في الترويسة — من أنت، وأيّ يوم وساعة يجري عليها عملك.
//
// **بيانات صاحب الجلسة وحده**: تُقرأ من كائن `me` الذي يعيده `/me` للمستخدم الحالي —
// لا نداء جديد، ولا قائمة موظفين تُجلب لاستخراج سطر منها، ولا معرّف داخلي يُعرض.
// وما لا يوجد لا يُخترع: هاتف غير مسجَّل يظهر «—» لا رقمًا مصنوعًا.
//
// **الساعة حيّة بتوقيت القاهرة، بنظام ١٢ ساعة مع AM/PM**: مؤقّت كل ثانية، والقراءة من `serverNow()` — أي ساعة
// **الخادم** مصحَّحةً بانحراف الجهاز عنه (يُقاس من ترويسة `Date` في كل ردّ)، فلا يعرض
// النظام وقتًا يخالف ما يُسجَّل به فعلًا. والتاريخ من نفس القراءة، فينقلب عند منتصف
// الليل بلا تحديث صفحة. والمؤقّت يُنظَّف عند التفكيك.
import { useEffect, useState } from 'react';
import { serverNow, type Me } from '../lib/api.ts';
import { cairoClock, localPhone } from '../lib/format.ts';
import { jobTitleOf } from '../lib/labels.ts';

/** الساعة والتاريخ بتوقيت القاهرة، يتجدّدان كل ثانية من ساعة الخادم. */
export function useCairoClock(): { date: string; time: string } {
  const [now, setNow] = useState(() => cairoClock(serverNow()));
  useEffect(() => {
    const id = setInterval(() => setNow(cairoClock(serverNow())), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1 leading-snug">
      <dt className="shrink-0 text-ink-500">{label} :</dt>
      <dd className="min-w-0 break-all text-ink-800">{value}</dd>
    </div>
  );
}

/** القيمة وحدها ظاهرة، وعنوانها لقارئ الشاشة (06/10/2026) — نفس بنية dt/dd التي تختبرها header-identity */
function BareLine({ label, value, strong = false, ltr = false }: { label: string; value: string; strong?: boolean; ltr?: boolean }) {
  return (
    <div className="flex leading-snug">
      <dt className="sr-only">{label} :</dt>
      <dd dir={ltr ? 'ltr' : undefined} className={`min-w-0 break-all ${strong ? 'font-bold text-ink-900' : 'text-ink-800'} ${ltr ? 'tabular-nums' : ''}`}>{value}</dd>
    </div>
  );
}

export function HeaderIdentity({ me, compact = false }: { me: Me; /** سطرٌ واحد لترويسة سطح المكتب (04/10/2026) */ compact?: boolean }) {
  const { date, time } = useCairoClock();

  /**
   * **سطرٌ واحد** (قرار المالك 04/10/2026: «الاطار ال فوق دا واكل من الشاشه كتير جدا… عاوز الاطار دا مياكلش من مساحة الشاشه
   * علشان الشاشه كلها تبقى واضحه للناس»): كانت البطاقة ثلاثة أسطر وستّة حقول في عمودين فتأخذ ثلث الشاشة على مقياس 90%.
   * نفس الحقول بنفس عناوينها (dt/dd كما تختبرها header-identity) لكن متجاورةً في سطرٍ واحد يلتفّ عند الضيق.
   */
  if (compact) {
    return (
      <div className="min-w-0 leading-tight">
        <div className="flex flex-wrap items-baseline gap-x-2">
          {/* الاسم التجاري مخفيّ عن قارئ الشاشة: بديل صورة الشعار يقوله (كما في العرض الكامل) */}
          <p className="text-sm font-bold text-brand-700" aria-hidden="true">iConTravel</p>
          <p className="truncate text-xs font-semibold text-ink-700">iConTravel Sales Management</p>
        </div>
        {/**
          * **بلا عناوين وبخطٍّ أكبر** (قرار المالك 06/10/2026: «بلاش نكتب name , title و phone number وهكذا علشان نوفر ف المساحة
          * وكمان علشان تكبر الخط شوية لانه صغير»): القيم وحدها بفواصل «·» — والعناوين باقيةٌ لقارئ الشاشة (sr-only) فلا يضيع معنى
          * كل قيمة. والاسم بخطٍّ عريض أوّلًا؛ والهاتف والبريد والوقت باتجاه LTR كي لا تنقلب أرقامها في سطرٍ عربي.
          */}
        <dl className="flex flex-wrap items-baseline gap-x-2 gap-y-0 text-[13px] text-ink-700
          [&>div:not(:last-child)]:after:ms-2 [&>div:not(:last-child)]:after:text-ink-300 [&>div:not(:last-child)]:after:content-['·']">
          {/* الاسم ثم المسمّى الوظيفي (0189، قرار المالك 05/10/2026) بدل «Sales Name» */}
          <BareLine label="Name" value={me.fullNameAr} strong />
          <BareLine label="Title" value={me.jobTitle ?? jobTitleOf(me)} />
          <BareLine label="Phone Number" value={localPhone(me.phone)} ltr />
          <BareLine label="Email" value={me.email} ltr />
          <BareLine label="Date" value={date} ltr />
          <div className="flex leading-snug">
            <dt className="sr-only">Time :</dt>
            <dd dir="ltr" className="tabular-nums text-ink-800" data-testid="header-clock">{time}</dd>
          </div>
        </dl>
      </div>
    );
  }

  return (
    <div className="min-w-0">
      {/* الاسم التجاري مكتوبًا بجانب الشعار: مخفيّ عن قارئ الشاشة لأن بديل الصورة يقوله
          أصلًا، فلا يُنطق مرّتين. أما «iConTravel Sales Management» فاسم النظام ويُقرأ. */}
      <p className="text-xl font-bold leading-tight text-brand-700" aria-hidden="true">iConTravel</p>
      <p className="text-base font-semibold leading-tight text-ink-700">iConTravel Sales Management</p>
      <dl className="mt-1 flex flex-wrap gap-x-6 gap-y-0.5 text-xs">
        <div className="min-w-0">
          <Line label="Name" value={me.fullNameAr} />
          <Line label="Title" value={me.jobTitle ?? jobTitleOf(me)} />
          <Line label="Phone Number" value={localPhone(me.phone)} />
          <Line label="Email" value={me.email} />
        </div>
        <div className="min-w-0">
          <Line label="Date" value={date} />
          {/* الثواني تتغيّر كل ثانية: `aria-live` مغلق عمدًا حتى لا يُقاطع قارئ الشاشة كل ثانية */}
          <div className="flex gap-1 leading-snug">
            <dt className="shrink-0 text-ink-500">Time :</dt>
            {/* `dir="ltr"`: في سطر عربي تُعيد خوارزمية الاتجاه ترتيب «01:34:18 AM» فتضع
                AM أوّلًا. والوقت وحدةٌ واحدة تُقرأ من اليسار، فيُثبَّت اتجاهها صراحةً. */}
            <dd dir="ltr" className="tabular-nums text-ink-800" data-testid="header-clock">{time}</dd>
          </div>
        </div>
      </dl>
    </div>
  );
}
